/**
 * TTS voice filter — next Gen, not live playback EQ.
 * Premium (Member) only in VoiceForge.
 */

export interface TtsVoiceFilter {
  /** 0.7–1.3, 1 = unchanged */
  speed: number;
  /** Semitones −4…+4, 0 = unchanged */
  pitch: number;
  /** 0–1, 0.5 = unchanged intensity */
  energy: number;
  /** 0–1, 0.5 = leave dial; 1 = flatten toward even */
  evenness: number;
}

export const DEFAULT_VOICE_FILTER: TtsVoiceFilter = {
  speed: 1,
  pitch: 0,
  energy: 0.5,
  evenness: 0.5,
};

export function clampVoiceFilter(raw: Partial<TtsVoiceFilter> | null | undefined): TtsVoiceFilter {
  const d = DEFAULT_VOICE_FILTER;
  const n = (v: unknown, lo: number, hi: number, fb: number) => {
    const x = Number(v);
    if (!Number.isFinite(x)) return fb;
    return Math.min(hi, Math.max(lo, x));
  };
  return {
    speed: n(raw?.speed, 0.7, 1.3, d.speed),
    pitch: n(raw?.pitch, -4, 4, d.pitch),
    energy: n(raw?.energy, 0, 1, d.energy),
    evenness: n(raw?.evenness, 0, 1, d.evenness),
  };
}

export function voiceFilterIsDefault(f: TtsVoiceFilter): boolean {
  const d = DEFAULT_VOICE_FILTER;
  return (
    Math.abs(f.speed - d.speed) < 0.02 &&
    Math.abs(f.pitch - d.pitch) < 0.15 &&
    Math.abs(f.energy - d.energy) < 0.03 &&
    Math.abs(f.evenness - d.evenness) < 0.03
  );
}

export function applyEvennessToDial(dial: number, evenness: number): number {
  const e = Math.min(1, Math.max(0, evenness));
  const d = Math.min(1, Math.max(0, dial));
  const t = (e - 0.5) * 2;
  if (t <= 0) return d;
  return d + (0.5 - d) * t;
}

export function voiceFilterEmotionSuffix(f: TtsVoiceFilter): string {
  const bits: string[] = [];
  if (f.speed <= 0.88) bits.push("slow");
  else if (f.speed >= 1.12) bits.push("fast");
  if (f.pitch <= -2) bits.push("low");
  else if (f.pitch >= 2) bits.push("high");
  if (f.energy <= 0.22) bits.push("whisper");
  else if (f.energy <= 0.38) bits.push("soft");
  else if (f.energy >= 0.78) bits.push("loud");
  return bits.join(" ");
}

export function filterToSynthParams(f: TtsVoiceFilter): {
  speed: number;
  pitch: number;
  volume: number;
} {
  const energy = applyEvennessToDial(f.energy, f.evenness);
  const volume = 0.55 + energy * 0.9;
  return {
    speed: Math.min(2, Math.max(0.5, f.speed)),
    pitch: Math.min(12, Math.max(-12, f.pitch)),
    volume: Math.min(2, Math.max(0.1, volume)),
  };
}

export function filterToBreezeInstructions(f: TtsVoiceFilter): string | undefined {
  const suffix = voiceFilterEmotionSuffix(f);
  if (!suffix) return undefined;
  return `Speak with a ${suffix.replace(/ /g, ", ")} delivery.`;
}
